// Boot phase timing collector (O1, #3986).
//
// Problem: `booting_time` was one total number. When a boot was slow we could
// not tell WHERE the time went (auth? couch login? singleton reads? nav
// restore?), so every speed fix was a guess. This module records one duration
// per boot phase and turns them into flat telemetry properties that ride on
// the existing `booting_time`, `splash_hang_detected`, and `SplashStuckOnBoot`
// payloads.
//
// How it is used:
// - `app.component.ts` calls `bootTimings.reset()` at the very start of the
//   boot sequence, then brackets each boot phase with `start(...)`/`end(...)`.
// - `auth.service.ts` brackets the cold-boot couch login, `user-db.service.ts`
//   brackets the singleton batch, and `home.ts` brackets the first home
//   render. They all import the shared `bootTimings` singleton below — the
//   module is deliberately not an Angular service so low-level code can
//   record phases without dependency-injection churn.
// - `bootTimings.snapshot()` is spread into the telemetry payloads. The hang
//   payload does it at the emit site in `app.component.ts`; the `booting_time`
//   payload is built by `booting-time-properties.ts`, which does it there for
//   both of that event's emit sites at once. Only phases that COMPLETED are
//   included, so a warm resume simply carries fewer phase properties than a
//   cold boot, and a hang payload shows every phase that finished before the
//   wedge (the stuck phase itself is in `boot_stage`).
//
// Semantics — "latest attempt wins":
// - `start(phase)` opens a new measurement and forgets any earlier duration
//   for that phase. This is deliberate: when a warm resume fails and falls
//   back to a cold start, the cold attempt re-runs the same phases and the
//   numbers we want are the ones from the attempt that actually finished the
//   boot. The same applies to mid-session re-initialization (for example
//   coach impersonation re-opens the user DB): the recorded value then
//   describes the latest splash window, which is what a later hang payload
//   is about. `design-docs/telemetry.md` documents this caveat.
// - `end(phase)` without a matching `start` is a no-op. That is not a
//   swallowed error: phase marks live in several files, and a path that
//   legitimately skips a phase's start (for example the warm path never
//   starts `scheduleFixes`) must not blow up the boot over telemetry.
//
// Durations come from an injectable monotonic clock (`performance.now` by
// default) so the numbers cannot jump when the wall clock changes, and so
// unit tests can drive time by hand. Keep this module free of Angular and
// browser-only imports — the unit test loads it under plain node (same
// pattern as splash-watchdog.ts).

// the phases of the measurement contract (PRD #3986 Step 16, extended by
// #4401). The enum VALUE is the exact spelling used in the telemetry property
// name, so it is part of the Mixpanel contract — do not rename values without
// updating design-docs/telemetry.md and the Mixpanel queries built on them.
//
// The last five are #4401's sub-steps. They all sit INSIDE the region that
// `boot_stage` reports as the single label `cold:userDb.init`, so a boot wedged
// anywhere in there used to produce one indistinguishable label. See
// design-docs/boot-sequence.md, "Naming the step that is wedged (#4401)".
export enum BootPhase {
  Auth = 'auth',
  CouchLogin = 'couchLogin',
  UserDbInit = 'userDbInit',
  Singletons = 'singletons',
  SharedDbs = 'sharedDbs',
  Plan = 'plan',
  ScheduleFixes = 'scheduleFixes',
  Finishing = 'finishing',
  /**
   * The consent / terms dialog wait (#4615). User-paced and unbounded, so it
   * is NOT boot work — but it sits inside `booting_time`'s total, and until
   * this bracket existed it was invisible there. Measured so an analyst can
   * subtract it rather than guess at it; never treat it as boot speed.
   */
  ConsentGate = 'consentGate',
  NavInit = 'navInit',
  FirstHomeRender = 'firstHomeRender',
  /** The one batched remote read of all boot singletons (L6/S2). */
  SingletonBatchRead = 'singletonBatchRead',
  /**
   * Writing pass-through-read docs into the local db. This is NESTED INSIDE
   * SingletonBatchRead, not a sibling of it — the outer phase's duration
   * contains this one's. Bracketed apart because the read is remote and bounded
   * while this write is local and is not, so without the split a slow network
   * and a local store that stopped answering look identical.
   * `getTypedRecordsByKeysAsync` has exactly one caller, the boot singleton
   * batch, so this phase can never open outside boot.
   */
  PassThroughGraft = 'passThroughGraft',
  /** The per-singleton read path, used only when the batch above failed. */
  SingletonFallback = 'singletonFallback',
  /** recoverMissingFitnessWeightAsync, which can re-read the plan. */
  RecoverFitnessWeight = 'recoverFitnessWeight',
  /** flushPendingMigrationSavesAsync — the writes a boot migration queued. */
  FlushMigrationSaves = 'flushMigrationSaves',
}

/**
 * What a `booting_time` event says it describes (#4615).
 *
 * The values are the wire spelling of the `boot_kind` property — same
 * convention as `PriorSessionHint` — so renaming one breaks saved Mixpanel
 * queries. Why the property exists at all: one app launch can emit several
 * `booting_time` events, and before this they were indistinguishable.
 */
export enum BootEmission {
  /** nothing reported yet in this page. */
  None = 'none',
  /** the login / welcome page came up; nobody is signed in. Not a boot of the app proper. */
  PreLogin = 'pre-login',
  /** a full cold boot measured from app launch — a returning user with a stored session. */
  Cold = 'cold',
  /** a full cold boot that followed a sign-in, measured from the sign-in (see restartWindowForSignIn). */
  ColdAfterSignIn = 'cold-after-sign-in',
  /** a warm resume. */
  Warm = 'warm',
  /** something re-reported after a full boot already finished in this page — a re-initialisation, not a boot. */
  Reinit = 'reinit',
}

// builds the telemetry property name for one phase, e.g. `phase_couchLogin_ms`
export function bootPhasePropertyName(phase: BootPhase): string {
  return `phase_${phase}_ms`;
}

// one open bracket: when it was opened on the clock, and where it sits in the
// order the currently-open brackets were opened. currentPhase() below says why
// the second field is needed and why the clock cannot stand in for it.
interface OpenBracket {
  startedAt: number;
  openOrder: number;
}

export class BootTimings {
  // open measurements: phase -> when it was opened, and in what order
  private open = new Map<BootPhase, OpenBracket>();
  // finished measurements: phase -> duration in ms
  private durations = new Map<BootPhase, number>();
  // stamped into each bracket as it opens. reset() restarts the numbering, so
  // that reset() really does forget everything — but nothing observable depends
  // on that: it empties the open map in the same breath, and currentPhase() only
  // ever compares brackets that are open right now. So a counter left climbing
  // across boot windows would give identical answers, and there is deliberately
  // no unit test for the restart.
  private nextOpenOrder = 0;
  // #4615: when the window `booting_time` reports opened, on the same monotonic
  // clock as the phases. Boot start by default; moved by restartWindowForSignIn.
  private windowStartedAt: number;
  // what the last `booting_time` in this page said it was, and whether the
  // window has already been handed to a sign-in. Both live here rather than in
  // app.component so the whole decision is pure and testable.
  private lastEmission = BootEmission.None;
  private windowRestartedForSignIn = false;

  constructor(private readonly now: () => number = () => performance.now()) {
    this.windowStartedAt = this.now();
  }

  // forget everything — called once at the top of the boot sequence so a
  // SPA-internal restart (e.g. the one-shot boot reload recovery lands on a
  // fresh page load anyway) never mixes two boots' numbers
  reset(): void {
    this.open.clear();
    this.durations.clear();
    this.nextOpenOrder = 0;
    // #4615: a fresh boot window too — start time, and the memory of what the
    // previous window reported. Without the last two, a SPA-internal restart
    // would inherit the old window's sign-in state.
    this.windowStartedAt = this.now();
    this.lastEmission = BootEmission.None;
    this.windowRestartedForSignIn = false;
  }

  // record a bracket as open right now, taking the next place in the open order
  private openBracket(phase: BootPhase): void {
    this.open.set(phase, { startedAt: this.now(), openOrder: this.nextOpenOrder++ });
  }

  start(phase: BootPhase): void {
    // if the phase ran before (warm attempt, earlier splash window), the new
    // attempt invalidates the old number — latest attempt wins
    this.durations.delete(phase);
    this.openBracket(phase);
  }

  // like start(), but only the FIRST measurement per boot window counts:
  // a no-op when the phase already completed or is currently open. Used for
  // `firstHomeRender`, where later re-entries to the home page must not
  // overwrite the boot's first-render number. reset() re-arms it, so the
  // once-per-window semantics live inside the collector's own contract
  // (unlike an external once-flag, which reset() could not see).
  startOnce(phase: BootPhase): void {
    // if the phase was already measured or is still running, keep it
    if (this.durations.has(phase) || this.open.has(phase)) {
      return;
    }
    this.openBracket(phase);
  }

  end(phase: BootPhase): void {
    const bracket = this.open.get(phase);
    // if there is no open measurement, there is nothing to record (see the
    // module comment — this is a legitimate skip, not an error path)
    if (bracket === undefined) {
      return;
    }
    this.open.delete(phase);
    this.durations.set(phase, this.now() - bracket.startedAt);
  }

  /**
   * The phase that is currently open, or undefined when none is.
   *
   * snapshot() below deliberately lists only phases that COMPLETED, so the one
   * thing a wedged boot's payload never said was which step it was actually
   * sitting in. This reads the open measurements the collector already keeps and
   * answers exactly that; app.component.ts reports it as `boot_step` (#4401).
   *
   * When phases nest — userDbInit brackets singletons, which brackets the
   * batched singleton read — the INNERMOST one is the useful answer, so this
   * returns the bracket that was opened last.
   *
   * It reads the open order the collector stamps in `openBracket()`, and neither
   * the clock nor Map order. Both of those give wrong answers here: start times
   * can tie because `performance.now()` is coarsened, and Map order is
   * first-insertion order — `start()` on a phase whose bracket is still open
   * re-uses that phase's existing Map slot, and a Map does not move a key to the
   * end when an existing key is re-set. The stamped counter never ties and never
   * disagrees with the order the brackets were actually opened in.
   */
  currentPhase(): BootPhase | undefined {
    let innermost: BootPhase | undefined = undefined;
    let latestOpenOrder = -1;
    for (const [phase, bracket] of this.open) {
      if (bracket.openOrder > latestOpenOrder) {
        latestOpenOrder = bracket.openOrder;
        innermost = phase;
      }
    }
    return innermost;
  }

  /**
   * Run `work` bracketed as `phase`, closing the bracket however it ends.
   *
   * Prefer this to a hand-written `start` / `try` / `finally` / `end` at the
   * call site: wrapping the call means the `end` cannot be forgotten on an
   * early return or a throw. Same reasoning as `trackLocalOpAsync` in
   * services/local-op-tracker.ts, and the same non-interference — the value and
   * any error pass straight through.
   *
   * "Latest attempt wins" still applies, so a nested or repeated phase behaves
   * exactly as it does with the explicit form.
   */
  async measureAsync<T>(phase: BootPhase, work: () => Promise<T>): Promise<T> {
    this.start(phase);
    try {
      return await work();
    } finally {
      this.end(phase);
    }
  }

  /**
   * How long the current boot window has been running, in whole ms — what
   * `booting_time` reports as `time_ms` (#4615).
   *
   * It reads the same monotonic clock the phases do, so the total and the
   * phases can be compared. The component used to subtract a wall-clock
   * `DateTime` instead, which could jump when the device clock changed.
   */
  elapsedSinceWindowStartMs(): number {
    return Math.round(this.now() - this.windowStartedAt);
  }

  /** Remember what the `booting_time` just emitted said it was. */
  noteEmission(kind: BootEmission): void {
    this.lastEmission = kind;
  }

  /**
   * Hand the boot window to a sign-in that has just happened, and say whether
   * that was done (#4615).
   *
   * It only fires when the last thing reported was the login page. That is the
   * one case where the window is wrong: the app already finished, took the
   * splash down and reported it, and everything since then was a person reading
   * and typing — not boot work. Over 30 days of production data that mismeasure
   * was 47 % of cold native boots, median 30 s, largest 74 minutes.
   *
   * It deliberately does NOT call reset(). The phases already recorded — `auth`
   * from the launch, and `couchLogin`, which is bracketed just after this call —
   * are real work the person waited for. Resetting would delete them.
   *
   * It fires at most once per window, because onLoginStateChange can run more
   * than once and a second move would throw away the couch login the first one
   * kept.
   */
  restartWindowForSignIn(): boolean {
    if (this.lastEmission !== BootEmission.PreLogin || this.windowRestartedForSignIn) {
      return false;
    }
    this.windowStartedAt = this.now();
    this.windowRestartedForSignIn = true;
    return true;
  }

  /**
   * What the `booting_time` about to be emitted should call itself (#4615).
   *
   * `signedIn` is whether a user is known at emit time; `warmResume` is whether
   * this boot took the warm path. The reinit case comes first on purpose: once a
   * full boot has reported, anything further in the same page is a
   * re-initialisation — the couch-login retry can cause one — and counting it as
   * a boot is what made those events launch-measured outliers.
   */
  nextEmissionKind({ signedIn, warmResume }: { signedIn: boolean, warmResume: boolean }): BootEmission {
    if (!signedIn) {
      return BootEmission.PreLogin;
    }
    if (this.lastEmission !== BootEmission.None && this.lastEmission !== BootEmission.PreLogin) {
      return BootEmission.Reinit;
    }
    if (warmResume) {
      return BootEmission.Warm;
    }
    return this.windowRestartedForSignIn ? BootEmission.ColdAfterSignIn : BootEmission.Cold;
  }

  // flat telemetry properties for every COMPLETED phase, rounded to whole ms.
  // Phases still running or never started are absent — the consumer can tell
  // "did not happen" and "still in flight at emit time" apart via boot_stage,
  // and which step is in flight via currentPhase() above.
  snapshot(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [phase, durationMs] of this.durations) {
      result[bootPhasePropertyName(phase)] = Math.round(durationMs);
    }
    return result;
  }
}

// the one shared collector instance for the app (see module comment)
export const bootTimings = new BootTimings();
